import { useState } from 'react';
import { useNavigate } from 'react-router';

import type {
  ClientFormValues,
  ModalOpenerType,
  OtpFormValues
} from '@/components/Shared/entities/client-otp-modal';
import { ROUTES } from '@/components/Shared/routes/routes';
import { CREATE_ROUTE_NAME } from '@/constants';
import { onlyDigits } from '@/services/formatter';
import { handleError } from '@/services/handleError';
import { useAppSelector } from '@/store';

import { ieLeasingApi } from '../../api';
import type { TLeasingOrder } from '../../types';

interface ValidateModalInterface {
  openedModal: ModalOpenerType;
  defaultPhone?: string;
  uuid?: string;
  clientComment?: string;
}

type TProps = {
  getRequestsPerPage?: () => void;
};

const BMG_ERROR_MESSAGE =
  'Введенный номер телефона не зарегистрирован на ИИН в Базе Мобильных Граждан';

const useValidateIeLeasingOtp = ({ getRequestsPerPage }: TProps) => {
  const navigate = useNavigate();

  const { dealer } = useAppSelector((state) => state.userInfo);

  const [isValidateModalOpened, setIsValidateModalOpened] =
    useState<ValidateModalInterface>({ openedModal: '' });

  const [clientInfo, setClientInfo] = useState<ClientFormValues | null>(null);
  const [requestId, setRequestId] = useState('');

  const onClientSubmit = async (values: ClientFormValues): Promise<string> => {
    if (!dealer) return '';

    setClientInfo(values);

    const splittedFullName = values.fullName
      .replace(/\s{2,}/g, ' ')
      .split(' ')
      .filter((data) => data.trim() !== '');

    const [lastName, firstName, ...restPartOfName] = splittedFullName;

    try {
      const { requestUuid } = await ieLeasingApi.createUnverifiedDraft({
        clientPhone: onlyDigits(values.phone),
        dealer_uuid: dealer.value,
        firstName: firstName || ' ',
        iin: values.iin,
        lastName: lastName || ' ',
        middleName: restPartOfName ? restPartOfName.join(' ') : ' ',
        type: values.sentWithType
      });

      getRequestsPerPage?.();
      setRequestId(requestUuid);

      return '';
    } catch (error) {
      let errorMessage = 'Произошла ошибка! Попробуйте позже';
      handleError(error, {
        onApiError: (err) => {
          errorMessage = err.message;
          if (errorMessage.includes(BMG_ERROR_MESSAGE)) {
            setIsValidateModalOpened({ openedModal: 'bmg-error' });
          }
        }
      });
      return errorMessage;
    }
  };

  const onOtpSubmit = async (values: OtpFormValues) => {
    const code = values.first + values.second + values.third + values.fourth;
    let errorMessage = 'Неверный код подтверждения!';

    try {
      const response = await ieLeasingApi.validateOtp({
        otp: code,
        uuid: requestId
      });

      if (response.result) {
        navigate(ROUTES.leasingIeRequest(CREATE_ROUTE_NAME), {
          state: { requestId }
        });
        return '';
      }

      if (response.message)
        return `Неверный код подтверждения! ${response.message}`;
    } catch (error) {
      handleError(error, {
        notify: false,
        onApiError: (err) => {
          if (err.message === 'Too many otp attempts') {
            getRequestsPerPage?.();
            errorMessage =
              'Превышено количество попыток ввода кода подтверждения';
          } else {
            errorMessage = 'Произошла ошибка! Попробуйте позже';
          }
        }
      });
    }

    return errorMessage;
  };

  const retryOTP = async (data?: {
    phone: string;
    type: 'WhatsApp' | 'SMS';
    uuid?: string;
  }): Promise<void> => {
    try {
      await ieLeasingApi.retryOTP({
        clientPhone: onlyDigits(clientInfo?.phone || data?.phone || ''),
        type: data?.type || 'WhatsApp',
        uuid: requestId || data?.uuid || ''
      });
    } catch (error) {
      handleError(error);
    }
  };

  const continueVerifying = async (row: TLeasingOrder) => {
    setRequestId(row.uuid);
    setIsValidateModalOpened({
      openedModal: 'otp-form',
      defaultPhone: row.client?.phoneNumber || '',
      uuid: row.uuid
    });
  };

  return {
    onClientSubmit,
    onOtpSubmit,
    retryOTP,
    continueVerifying,
    isValidateModalOpened,
    setIsValidateModalOpened
  };
};

export default useValidateIeLeasingOtp;
